import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Mail, MapPin, ArrowRight, Send, MessageSquare, ShieldCheck, Clock } from "lucide-react";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MOTIVOS = [
  "Consulta general sobre Ley 21.719",
  "Soporte técnico (escáner, generador, descarga)",
  "Facturación / Módulo Pro",
  "Demo para empresa o agencia",
  "Prensa / partnerships",
  "Otro",
];

export default function Contacto() {
  const [nombre, setNombre] = useState("");
  const [email, setEmail] = useState("");
  const [empresa, setEmpresa] = useState("");
  const [motivo, setMotivo] = useState(MOTIVOS[0]);
  const [mensaje, setMensaje] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !mensaje.trim()) {
      toast.error("Ingresa al menos email y mensaje.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contacto`, {
        nombre: nombre.trim() || null,
        email: email.trim().toLowerCase(),
        empresa: empresa.trim() || null,
        motivo,
        mensaje: mensaje.trim(),
      });
      setSent(true);
      toast.success("Mensaje enviado. Te responderemos en máximo 24h hábiles.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === "string") toast.error(detail);
      else toast.error("No pudimos enviar tu mensaje. Escríbenos a privacidad@leydedatoschile.com");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Contacto"
        description="Habla con el equipo de Ley de Datos Chile. Soporte, demos para empresas y agencias, partnerships. Respondemos en máximo 24h hábiles."
        path="/contacto"
      />

      <section className="min-h-[calc(100vh-64px)] grid grid-cols-1 lg:grid-cols-2">
        {/* Left: form */}
        <div className="px-6 py-16 sm:py-20 lg:py-24">
          <div className="w-full max-w-xl mx-auto">
            <p className="font-mono text-[10px] tracking-[0.22em] text-blue-700 uppercase mb-6">
              Contacto
            </p>
            <h1 className="font-display text-4xl sm:text-5xl text-slate-900 leading-tight tracking-tight">
              Habla con <em className="italic text-blue-700">el equipo</em>.
            </h1>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Soporte técnico, demos para empresas/agencias, dudas de cumplimiento o partnerships. Respondemos en máximo 24h hábiles.
            </p>

            {sent ? (
              <div
                data-testid="contacto-success"
                className="mt-10 p-6 rounded-2xl border border-emerald-200 bg-emerald-50"
              >
                <p className="font-display text-xl text-emerald-900">Recibimos tu mensaje.</p>
                <p className="mt-2 text-sm text-emerald-800">
                  Te respondemos a <strong>{email}</strong> en menos de 24h hábiles.
                </p>
                <button
                  onClick={() => { setSent(false); setMensaje(""); }}
                  data-testid="contacto-otro"
                  className="mt-4 text-sm text-emerald-700 underline hover:text-emerald-900"
                >
                  Enviar otro mensaje
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-10 space-y-5">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block font-mono text-[10px] tracking-[0.18em] uppercase text-slate-500 mb-2">Nombre</label>
                    <input
                      data-testid="contacto-nombre"
                      type="text"
                      value={nombre}
                      onChange={(e) => setNombre(e.target.value)}
                      placeholder="María Pérez"
                      className="w-full h-12 px-4 bg-white border border-slate-300 rounded-xl focus:border-blue-600 focus:ring-2 focus:ring-blue-100 focus:outline-none transition-all"
                    />
                  </div>
                  <div>
                    <label className="block font-mono text-[10px] tracking-[0.18em] uppercase text-slate-500 mb-2">Empresa</label>
                    <input
                      data-testid="contacto-empresa"
                      type="text"
                      value={empresa}
                      onChange={(e) => setEmpresa(e.target.value)}
                      placeholder="Empresa SpA"
                      className="w-full h-12 px-4 bg-white border border-slate-300 rounded-xl focus:border-blue-600 focus:ring-2 focus:ring-blue-100 focus:outline-none transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label className="block font-mono text-[10px] tracking-[0.18em] uppercase text-slate-500 mb-2">Email *</label>
                  <input
                    data-testid="contacto-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tu@empresa.cl"
                    className="w-full h-12 px-4 bg-white border border-slate-300 rounded-xl focus:border-blue-600 focus:ring-2 focus:ring-blue-100 focus:outline-none transition-all"
                  />
                </div>

                <div>
                  <label className="block font-mono text-[10px] tracking-[0.18em] uppercase text-slate-500 mb-2">Motivo</label>
                  <select
                    data-testid="contacto-motivo"
                    value={motivo}
                    onChange={(e) => setMotivo(e.target.value)}
                    className="w-full h-12 px-4 bg-white border border-slate-300 rounded-xl focus:border-blue-600 focus:ring-2 focus:ring-blue-100 focus:outline-none transition-all"
                  >
                    {MOTIVOS.map((m) => <option key={m}>{m}</option>)}
                  </select>
                </div>

                <div>
                  <label className="block font-mono text-[10px] tracking-[0.18em] uppercase text-slate-500 mb-2">Mensaje *</label>
                  <textarea
                    data-testid="contacto-mensaje"
                    required
                    value={mensaje}
                    onChange={(e) => setMensaje(e.target.value)}
                    rows={5}
                    placeholder="Cuéntanos en qué te podemos ayudar..."
                    className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl focus:border-blue-600 focus:ring-2 focus:ring-blue-100 focus:outline-none transition-all resize-none"
                  />
                </div>

                <button
                  data-testid="contacto-submit"
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 rounded-full bg-slate-900 hover:bg-slate-800 active:scale-[0.98] disabled:opacity-60 text-white font-semibold transition-all inline-flex items-center justify-center gap-2"
                >
                  {loading ? "Enviando…" : (<>Enviar mensaje <Send className="w-4 h-4" /></>)}
                </button>

                <p className="text-xs text-slate-500 leading-relaxed">
                  Al enviar aceptas nuestra{" "}
                  <Link to="/politica-privacidad" className="underline hover:text-slate-700">política de privacidad</Link>.
                  Tus datos se usan solo para responderte.
                </p>
              </form>
            )}
          </div>
        </div>

        {/* Right: dark brand panel */}
        <div className="relative hidden lg:flex bg-ink-950 text-white overflow-hidden">
          <div className="absolute inset-0 bg-grid-pattern opacity-30 pointer-events-none" />
          <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full bg-blue-600/10 blur-3xl pointer-events-none" />
          <div className="relative flex flex-col justify-center px-12 max-w-xl">
            <div className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] text-blue-300 uppercase mb-8">
              <MessageSquare className="w-3.5 h-3.5" />
              Otras vías
            </div>
            <h2 className="font-display text-4xl xl:text-5xl text-white leading-tight">
              Escríbenos directo. Sin <em className="italic text-blue-300">intermediarios</em>.
            </h2>
            <p className="mt-6 text-slate-300 leading-relaxed text-sm max-w-md">
              Soporte humano. Sin chatbots. El equipo de Ley de Datos Chile responde personalmente cada mensaje.
            </p>

            <ul className="mt-10 space-y-5">
              <li className="flex items-start gap-3 text-sm">
                <Mail className="w-4 h-4 text-blue-300 mt-0.5 shrink-0" />
                <div>
                  <p className="font-mono text-[10px] tracking-[0.18em] text-blue-300 uppercase mb-1">Email general</p>
                  <a href="mailto:hola@leydedatoschile.com" className="text-slate-200 hover:text-white transition-colors">
                    hola@leydedatoschile.com
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3 text-sm">
                <ShieldCheck className="w-4 h-4 text-blue-300 mt-0.5 shrink-0" />
                <div>
                  <p className="font-mono text-[10px] tracking-[0.18em] text-blue-300 uppercase mb-1">Privacidad · ARCO</p>
                  <a href="mailto:privacidad@leydedatoschile.com" className="text-slate-200 hover:text-white transition-colors">
                    privacidad@leydedatoschile.com
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3 text-sm">
                <MapPin className="w-4 h-4 text-blue-300 mt-0.5 shrink-0" />
                <div>
                  <p className="font-mono text-[10px] tracking-[0.18em] text-blue-300 uppercase mb-1">Ubicación</p>
                  <span className="text-slate-200">Santiago, Chile</span>
                </div>
              </li>
              <li className="flex items-start gap-3 text-sm">
                <Clock className="w-4 h-4 text-blue-300 mt-0.5 shrink-0" />
                <div>
                  <p className="font-mono text-[10px] tracking-[0.18em] text-blue-300 uppercase mb-1">SLA respuesta</p>
                  <span className="text-slate-200">24h hábiles · L–V 9:00 a 18:00 (CLT)</span>
                </div>
              </li>
            </ul>

            <div className="mt-12 pt-8 border-t border-white/10">
              <p className="font-mono text-[10px] text-slate-500 uppercase tracking-widest mb-2">
                ¿Prefieres probar primero?
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 text-sm text-blue-300 hover:text-white transition-colors"
              >
                Escanear sin contacto · 3 gratis <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
